import { useParams, Navigate, Link } from "react-router-dom";
import {
  ArrowRight,
  ShoppingBag,
  ExternalLink,
  MessageCircle,
  Phone,
  CheckCircle2,
  CalendarCheck,
  Sparkles,
  Users,
  ClipboardList,
  Award,
  Timer,
  Snowflake,
  Calendar,
  MapPin,
  Wind,
  Target,
  Zap,
  ShieldCheck,
  UserCheck,
  Cpu,
  Smile,
  Repeat,
  Lightbulb,
  AlertTriangle,
  Info,
  Scissors,
  Clock,
  Sun,
  Baby,
  Activity,
  Search,
  Stethoscope,
  Flame,
  Camera,
  Heart,
} from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import FAQ, { faqJsonLd } from "@/components/FAQ";
import services from "@/data/services.json";
import products from "@/data/products.json";
import blog from "@/data/blog.json";
import categories from "@/data/categories.json";
import { SITE, waHref } from "@/data/site";

/**
 * Icon map for rich content sections declared in services.json.
 * Falls back to a neutral icon when the name is unknown so a data typo never breaks the page.
 */
const ICON_MAP = {
  sparkles: Sparkles,
  users: Users,
  clipboard: ClipboardList,
  "clipboard-list": ClipboardList,
  award: Award,
  timer: Timer,
  clock: Clock,
  snowflake: Snowflake,
  calendar: Calendar,
  "calendar-check": CalendarCheck,
  "map-pin": MapPin,
  wind: Wind,
  target: Target,
  zap: Zap,
  shield: ShieldCheck,
  "shield-check": ShieldCheck,
  "user-check": UserCheck,
  cpu: Cpu,
  smile: Smile,
  repeat: Repeat,
  lightbulb: Lightbulb,
  "alert-triangle": AlertTriangle,
  info: Info,
  scissors: Scissors,
  sun: Sun,
  baby: Baby,
  activity: Activity,
  search: Search,
  stethoscope: Stethoscope,
  flame: Flame,
  camera: Camera,
  heart: Heart,
  check: CheckCircle2,
};

function RichIcon({ name, size = 20, className = "" }) {
  const Cmp = ICON_MAP[name] || Sparkles;
  return <Cmp size={size} className={className} aria-hidden="true" />;
}

/**
 * Parse "[label](/url)" style inline links inside a plain text string.
 * Returns an array of strings and <Link>/<a> elements suitable for React children.
 */
function parseMarkdownLinks(text) {
  if (!text || typeof text !== "string") return text;
  const parts = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let m;
  let k = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    const label = m[1];
    const url = m[2];
    const external = /^(https?:)?\/\//i.test(url) || url.startsWith("mailto:") || url.startsWith("tel:");
    if (external) {
      parts.push(<a key={`md-lnk-${k++}`} href={url} target="_blank" rel="noreferrer" className="prose-link">{label}</a>);
    } else {
      parts.push(<Link key={`md-lnk-${k++}`} to={url} className="prose-link">{label}</Link>);
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length ? parts : text;
}

function parseInlineLinks(text) {
  if (!text || typeof text !== "string") return text;
  const htmlAnchorRegex = /<a\s+href="([^"]+)"[^>]*>([^<]+)<\/a>/g;
  if (htmlAnchorRegex.test(text)) {
    htmlAnchorRegex.lastIndex = 0;
    const parts = [];
    let lastIndex = 0;
    let m;
    let k = 0;
    while ((m = htmlAnchorRegex.exec(text)) !== null) {
      if (m.index > lastIndex) parts.push(parseMarkdownLinks(text.slice(lastIndex, m.index)));
      const url = m[1];
      const label = m[2];
      const external = /^(https?:)?\/\//i.test(url) || url.startsWith("mailto:") || url.startsWith("tel:");
      if (external) {
        parts.push(<a key={`lnk-${k++}`} href={url} target="_blank" rel="noreferrer" className="prose-link">{label}</a>);
      } else {
        parts.push(<Link key={`lnk-${k++}`} to={url} className="prose-link">{label}</Link>);
      }
      lastIndex = htmlAnchorRegex.lastIndex;
    }
    if (lastIndex < text.length) parts.push(parseMarkdownLinks(text.slice(lastIndex)));
    return parts;
  }
  return parseMarkdownLinks(text);
}

/**
 * Render the article content and INTERLEAVE additional images at natural break points
 * so the body doesn't feel like a wall of text with images dumped below.
 */
function ServiceGallerySlider({ images, title = "Örnek İşlemler" }) {
  if (!Array.isArray(images) || images.length <= 1) return null;
  const galleryImages = images.slice(1);
  if (galleryImages.length === 0) return null;

  return (
    <section className="py-16 md:py-20 bg-white border-y border-[#E5EAF0]" data-testid="service-gallery">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="chip mb-3 mx-auto">Galeri</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#0B1B33] tracking-tight">{title}</h2>
          <p className="text-[#4B5A6E] text-sm md:text-base mt-2">Merkezimizde uygulanan profesyonel çalışmalardan kareler</p>
        </div>
        <div className="flex items-center justify-between mb-3 px-1 sm:hidden">
          <span className="text-xs text-[#4B5A6E] font-medium flex items-center gap-1">
            <span>←</span> Kaydırmak için kaydırın <span>→</span>
          </span>
          <span className="text-[10px] tracking-wider uppercase bg-[#EEF6F1] text-[#10B981] px-2.5 py-1 rounded-full font-semibold">Tüm Kareler</span>
        </div>
        <div className="flex gap-6 overflow-x-auto pb-6 pt-2 no-scrollbar snap-x snap-mandatory">
          {galleryImages.map((img, i) => (
            <div key={i} className="min-w-[280px] sm:min-w-[320px] md:min-w-[360px] max-w-[400px] flex-shrink-0 snap-center bg-white rounded-2xl border border-[#E5EAF0] shadow-sm hover:shadow-md transition-all overflow-hidden group">
              <div className="aspect-[4/5] bg-[#F5F7FA] overflow-hidden relative">
                <img
                  src={img.startsWith("http") || img.startsWith("/") ? img : `/${img}`}
                  alt={`Örnek İşlem ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent flex items-end p-5 pointer-events-none">
                  <span className="text-white text-xs font-semibold uppercase tracking-wider bg-[#10B981] px-3 py-1 rounded-full shadow">Nail Artist - Selin Salur</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContentWithImages({ paragraphs, images, mainImage, title }) {
  if (!paragraphs || paragraphs.length === 0) return null;

  // Strip out obvious crawl leftovers: trailing sequence of h2 tags with no text under them.
  let clean = [...paragraphs];
  while (clean.length && clean[clean.length - 1].tag === "h2") {
    clean.pop();
  }

  // Deduplicate images:
  const seen = new Set();
  const extras = (images || [])
    .filter((s) => s && s !== mainImage)
    .filter((s) => {
      const key = (s.split("/").pop() || s).replace(/\.(jpe?g|png|webp)$/i, "");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 3);

  // Decide insertion points ensuring we insert after paragraphs (p) rather than right after headings
  const contentParagraphs = clean.filter((p) => p.tag !== "h4");
  const insertAt = extras.length
    ? extras.map((_, idx) => {
        // Find indices where tag === 'p'
        const pIndices = contentParagraphs
          .map((p, pIdx) => (p.tag === 'p' ? pIdx : -1))
          .filter((pIdx) => pIdx !== -1);
        if (pIndices.length === 0) return Math.floor(((idx + 1) * contentParagraphs.length) / (extras.length + 1));
        const targetP = Math.floor(((idx + 1) * pIndices.length) / (extras.length + 1));
        return pIndices[Math.min(targetP, pIndices.length - 1)];
      })
    : [];

  // Group consecutive <li> entries into a single <ul>
  const output = [];
  let imgIdx = 0;
  let liBuffer = [];
  const flushList = (keyPrefix) => {
    if (liBuffer.length > 0) {
      output.push(
        <ul key={`ul-${keyPrefix}`}>
          {liBuffer.map((t, j) => <li key={`li-${keyPrefix}-${j}`}>{parseInlineLinks(t)}</li>)}
        </ul>
      );
      liBuffer = [];
    }
  };

  contentParagraphs.forEach((p, i) => {
    if (p.tag === "li") {
      liBuffer.push(p.text);
    } else {
      flushList(i);
      if (p.tag === "h2") output.push(<h2 key={`p${i}`}>{parseInlineLinks(p.text)}</h2>);
      else if (p.tag === "h3") output.push(<h3 key={`p${i}`}>{parseInlineLinks(p.text)}</h3>);
      else if (p.text && p.text.includes("4 adımlı sade rutin yeterlidir")) {
        output.push(
          <div key={`routine-${i}`} className="my-8">
            <p className="text-[#4B5A6E] mb-6 leading-relaxed">Çok fazla ürün kullanmak her zaman daha iyi sonuç vermez. Yeni başlayanlar için 4 adımlı sade rutin yeterlidir:</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { step: "01", title: "Nazik Temizleme", desc: "Günlük kir, makyaj ve sebum kalıntılarından cildi arındırma." },
                { step: "02", title: "Tonik / Yatıştırıcı Su", desc: "Cilt pH dengesini koruma, gözenekleri yatıştırma ve ferahlatma." },
                { step: "03", title: "Nem Serumu", desc: "Hiyalüronik asit veya aktif desteklerle cilde yoğun nem kazandırma." },
                { step: "04", title: "Nemlendirici", desc: "Cilt tipine uygun kremle bariyeri onarma ve nemi hapsetme." }
              ].map((stepItem, sIdx) => (
                <div key={sIdx} className="bg-[#F8FAFC] border border-[#E5EAF0] rounded-2xl p-5 flex items-start gap-4">
                  <span className="w-10 h-10 rounded-xl bg-[#10B981]/10 text-[#10B981] font-display font-bold flex items-center justify-center shrink-0">{stepItem.step}</span>
                  <div>
                    <h4 className="font-display text-base text-[#0B1B33] font-semibold mb-1">{stepItem.title}</h4>
                    <p className="text-xs text-[#4B5A6E] leading-relaxed">{stepItem.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }
      else output.push(<p key={`p${i}`}>{parseInlineLinks(p.text)}</p>);
    }

    if (imgIdx < extras.length && insertAt[imgIdx] === i) {
      flushList(`img-${i}`);
      const src = extras[imgIdx];
      output.push(
        <figure key={`f${i}`} className="my-8 text-center">
          <img src={`/${src}`} alt={`${title} - ${imgIdx + 2}`} loading="lazy" className="max-h-[600px] w-auto h-auto object-contain mx-auto rounded-xl block" />
        </figure>
      );
      imgIdx++;
    }
  });
  flushList("end");

  return <div className="prose-medi max-w-none">{output}</div>;
}

/* -----------------------------------------------------------------------------
 * Rich sections — only rendered when the corresponding data exists on the item.
 * All components are safe no-ops for services that don't declare the field, so
 * existing pages stay pixel-identical.
 * -------------------------------------------------------------------------- */

function HeroBadges({ badges }) {
  if (!Array.isArray(badges) || badges.length === 0) return null;
  return (
    <ul data-testid="hero-badges" className="grid grid-cols-2 gap-x-4 gap-y-2.5 mb-7 max-w-md">
      {badges.map((b, i) => {
        const text = typeof b === "string" ? b : (b.text || "");
        const icon = typeof b === "object" && b.icon ? b.icon : "check";
        return (
          <li key={i} className="flex items-center gap-2 text-[13px] md:text-sm text-[#0B1B33]">
            <span className="h-6 w-6 rounded-full bg-[#10B981]/12 text-[#10B981] flex items-center justify-center shrink-0">
              <RichIcon name={icon} size={14} />
            </span>
            <span className="font-medium leading-snug">{text}</span>
          </li>
        );
      })}
    </ul>
  );
}

function QuickFactsBar({ facts }) {
  if (!Array.isArray(facts) || facts.length === 0) return null;
  return (
    <section data-testid="quick-facts" className="py-10 md:py-14 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {facts.map((f, i) => (
            <div
              key={i}
              className="group relative bg-white border border-[#E5EAF0] rounded-2xl p-4 md:p-5 hover:border-[#10B981]/40 hover:shadow-md transition-all"
            >
              <div className="h-9 w-9 rounded-xl bg-[#EEF6F1] text-[#059669] flex items-center justify-center mb-3">
                <RichIcon name={f.icon} size={18} />
              </div>
              <p className="text-[10px] tracking-[0.14em] uppercase text-[#7C8CA0] font-semibold mb-1.5 leading-tight">
                {f.label}
              </p>
              <p className="text-[13px] md:text-sm text-[#0B1B33] font-semibold leading-snug">
                {f.value}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SuitabilitySection({ data }) {
  if (!data || !Array.isArray(data.items) || data.items.length === 0) return null;
  return (
    <section data-testid="suitability" className="py-16 md:py-20 bg-[#F5F7FA] border-y border-[#E5EAF0]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-10 md:mb-12">
          <p className="chip-green mb-3">Uygunluk</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#0B1B33] tracking-tight leading-tight">
            {data.title || "Kimler İçin Uygundur?"}
          </h2>
          {data.intro ? (
            <p className="mt-4 text-[#4B5A6E] leading-relaxed">{data.intro}</p>
          ) : null}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {data.items.map((it, i) => (
            <div
              key={i}
              className="bg-white border border-[#E5EAF0] rounded-2xl p-5 md:p-6 hover:border-[#10B981]/40 hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-xl bg-[#10B981]/10 text-[#10B981] flex items-center justify-center shrink-0">
                  <RichIcon name={it.icon || "check"} size={20} />
                </div>
                <div>
                  <p className="font-display text-lg text-[#0B1B33] font-semibold leading-snug mb-1">
                    {it.title}
                  </p>
                  {it.desc ? (
                    <p className="text-sm text-[#4B5A6E] leading-relaxed" dangerouslySetInnerHTML={{
                      __html: it.desc.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#10B981] underline font-medium hover:opacity-80" target="_blank" rel="noopener noreferrer">$1</a>')
                    }} />
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProcessTimeline({ steps, title }) {
  if (!Array.isArray(steps) || steps.length === 0) return null;
  return (
    <section data-testid="process-timeline" className="py-20 md:py-24 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="chip mb-3 mx-auto">Uygulama Süreci</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#0B1B33] tracking-tight leading-tight">
            {title || "Nasıl İlerliyor?"}
          </h2>
        </div>
        <ol className="relative">
          <div
            className="absolute left-5 top-3 bottom-3 w-px bg-gradient-to-b from-[#10B981]/0 via-[#10B981]/50 to-[#10B981]/0"
            aria-hidden="true"
          />
          {steps.map((s, i) => (
            <li key={i} className="relative pl-16 pb-6 last:pb-0">
              <span className="absolute left-0 top-1 h-10 w-10 rounded-full bg-white border-2 border-[#10B981] text-[#0B1B33] font-display font-semibold flex items-center justify-center shadow-sm text-sm">
                {s.n || i + 1}
              </span>
              <div className="bg-white border border-[#E5EAF0] rounded-2xl p-5 md:p-6 hover:border-[#10B981]/40 hover:shadow-md transition-all">
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#4F8B2A] font-bold mb-1.5">
                  Adım {s.n || i + 1}
                </p>
                <p className="font-display text-lg md:text-xl text-[#0B1B33] font-semibold leading-snug mb-2">
                  {s.title}
                </p>
                {s.desc ? (
                  <p className="text-sm text-[#4B5A6E] leading-relaxed">{s.desc}</p>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function CalloutStack({ callouts }) {
  if (!Array.isArray(callouts) || callouts.length === 0) return null;
  const styles = {
    tip: { bg: "bg-[#EEF6F1]", border: "border-[#10B981]/25", text: "text-[#065F46]", icon: "text-[#10B981]" },
    warn: { bg: "bg-[#FDF6EC]", border: "border-[#C9A24B]/40", text: "text-[#6B4E14]", icon: "text-[#C9A24B]" },
    info: { bg: "bg-[#EEF3FA]", border: "border-[#2C6BB0]/25", text: "text-[#0E2B4F]", icon: "text-[#2C6BB0]" },
    success: { bg: "bg-[#EEF6F1]", border: "border-[#10B981]/25", text: "text-[#065F46]", icon: "text-[#10B981]" },
  };
  return (
    <section data-testid="callouts" className="py-4 md:py-6 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
        {callouts.map((c, i) => {
          const s = styles[c.variant] || styles.info;
          return (
            <div
              key={i}
              className={`flex items-start gap-4 ${s.bg} border ${s.border} rounded-2xl p-5 md:p-6`}
            >
              <div className={`shrink-0 h-9 w-9 rounded-full bg-white/70 ${s.icon} flex items-center justify-center`}>
                <RichIcon name={c.icon || "info"} size={18} />
              </div>
              <div className={`text-[15px] leading-relaxed ${s.text}`}>
                {c.title ? (
                  <p className="font-semibold text-[#0B1B33] mb-1">{c.title}</p>
                ) : null}
                <p dangerouslySetInnerHTML={{
                  __html: (c.text || "").replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="underline font-medium hover:opacity-80" target="_blank" rel="noopener noreferrer">$1</a>')
                }} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function FeatureCards({ features, title, kicker = "Teknik Özellikler" }) {
  if (!Array.isArray(features) || features.length === 0) return null;
  return (
    <section data-testid="feature-cards" className="py-20 md:py-24 bg-[#0E2B4F] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: "radial-gradient(circle at 20% 20%, #10B981 0, transparent 40%), radial-gradient(circle at 80% 60%, #2C6BB0 0, transparent 40%)"
      }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-2xl mb-10 md:mb-14">
          <p className="text-[11px] tracking-[0.22em] uppercase text-[#A8C520] font-bold mb-3">{kicker}</p>
          <h2 className="font-display text-3xl md:text-4xl text-white tracking-tight leading-tight">
            {title || "Cihaz ve Uygulama Detayları"}
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {features.map((f, i) => (
            <div
              key={i}
              className="group bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-[#10B981]/60 hover:bg-white/[0.06] transition-all"
            >
              <div className="h-11 w-11 rounded-xl bg-[#10B981]/15 text-[#10B981] flex items-center justify-center mb-4 group-hover:bg-[#10B981]/25 transition-colors">
                <RichIcon name={f.icon} size={22} />
              </div>
              <p className="font-display text-lg md:text-xl text-white font-semibold leading-snug mb-2">
                {f.title}
              </p>
              {f.desc ? (
                <p className="text-[14px] text-white/70 leading-relaxed">{f.desc}</p>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyUsGrid({ items, title }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <section data-testid="why-us" className="py-20 md:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="chip-green mb-3 mx-auto">Neden MediEste</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#0B1B33] tracking-tight leading-tight">
            {title || "Bizi Tercih Etmenizin Nedenleri"}
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {items.map((it, i) => (
            <div
              key={i}
              className="bg-[#F5F7FA] border border-[#E5EAF0] rounded-2xl p-6 md:p-7 hover:bg-white hover:border-[#10B981]/40 hover:shadow-lg transition-all"
            >
              <div className="h-12 w-12 rounded-2xl bg-white text-[#0E2B4F] border border-[#E5EAF0] flex items-center justify-center mb-5 shadow-sm">
                <RichIcon name={it.icon || "check"} size={22} />
              </div>
              <p className="font-display text-xl text-[#0B1B33] font-semibold leading-snug mb-2">
                {it.title}
              </p>
              {it.desc ? (
                <p className="text-[14px] text-[#4B5A6E] leading-relaxed">{it.desc}</p>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BeforeAfterPlaceholder({ data }) {
  if (!data) return null;
  const slides = Array.isArray(data.slides) ? data.slides : [];
  return (
    <section data-testid="before-after" className="py-20 md:py-24 bg-[#F5F7FA] border-y border-[#E5EAF0]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-10 md:mb-12">
          <p className="chip mb-3">Öncesi / Sonrası</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#0B1B33] tracking-tight leading-tight">
            {data.title || "Gerçek Sonuçlar"}
          </h2>
          {data.intro ? (
            <p className="mt-4 text-[#4B5A6E] leading-relaxed">{data.intro}</p>
          ) : null}
        </div>
        {slides.length > 0 ? (
          <div className="grid md:grid-cols-3 gap-5 md:gap-6">
            {slides.map((s, i) => (
              <figure key={i} className="bg-white rounded-2xl border border-[#E5EAF0] overflow-hidden">
                <div className="grid grid-cols-2 gap-px bg-[#E5EAF0]">
                  <div className="aspect-square bg-[#F5F7FA] relative">
                    {s.before ? <img src={`/${s.before}`} alt={`Öncesi ${i + 1}`} loading="lazy" className="w-full h-full object-cover" /> : null}
                    <span className="absolute top-2 left-2 text-[10px] tracking-[0.2em] uppercase bg-white/95 px-2 py-1 rounded-full font-bold text-[#0B1B33]">Öncesi</span>
                  </div>
                  <div className="aspect-square bg-[#F5F7FA] relative">
                    {s.after ? <img src={`/${s.after}`} alt={`Sonrası ${i + 1}`} loading="lazy" className="w-full h-full object-cover" /> : null}
                    <span className="absolute top-2 right-2 text-[10px] tracking-[0.2em] uppercase bg-[#10B981] text-white px-2 py-1 rounded-full font-bold">Sonrası</span>
                  </div>
                </div>
                {s.caption ? (
                  <figcaption className="p-4 text-sm text-[#4B5A6E] leading-relaxed">{s.caption}</figcaption>
                ) : null}
              </figure>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-dashed border-[#D1D9E4] rounded-2xl p-10 md:p-14 text-center">
            <div className="h-14 w-14 rounded-full bg-[#F5F7FA] text-[#7C8CA0] mx-auto mb-4 flex items-center justify-center">
              <Camera size={22} aria-hidden="true" />
            </div>
            <p className="text-[#0B1B33] font-display text-lg md:text-xl font-semibold mb-2">
              {data.placeholderTitle || "Öncesi / Sonrası görsellerimiz yakında"}
            </p>
            <p className="text-sm text-[#4B5A6E] leading-relaxed max-w-md mx-auto">
              {data.placeholderText || "Uygun izinler alınarak yayımlanacak gerçek uygulama görselleri burada listelenecektir. Kliniğimizde çekilmiş kendi vakalarımızdan sonuçlar için WhatsApp üzerinden bilgi talep edebilirsiniz."}
            </p>
            <a
              href={waHref("Merhaba, lazer epilasyon öncesi / sonrası görsellerini görebilir miyim?")}
              target="_blank"
              rel="noreferrer"
              className="btn-mint mt-6 inline-flex"
              data-testid="before-after-cta"
            >
              <MessageCircle size={14} /> WhatsApp ile İste
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

function FinalCTA({ data }) {
  if (!data) return null;
  return (
    <section data-testid="final-cta" className="py-20 md:py-24 bg-gradient-to-br from-[#0E2B4F] via-[#123564] to-[#0E2B4F] relative overflow-hidden">
      <div className="absolute -top-24 -right-20 h-72 w-72 rounded-full bg-[#10B981]/20 blur-3xl" aria-hidden="true" />
      <div className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-[#2C6BB0]/25 blur-3xl" aria-hidden="true" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-[11px] tracking-[0.24em] uppercase text-[#A8C520] font-bold mb-4">Randevu</p>
        <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight leading-tight mb-5">
          {data.title || "Pürüzsüz Bir Cilt İçin İlk Adımı Atın"}
        </h2>
        {data.subtitle ? (
          <p className="text-white/75 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10">
            {data.subtitle}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-3 justify-center">
          <a href={`tel:${SITE.phoneRaw}`} className="btn-mint" data-testid="final-cta-call">
            <Phone size={14} /> {SITE.phone}
          </a>
          <a
            href={waHref(`Merhaba, "${data.serviceTitle || "hizmet"}" hakkında bilgi almak istiyorum.`)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-white text-[#0E2B4F] text-xs tracking-[0.18em] uppercase font-semibold hover:bg-[#F5F7FA] transition-colors"
            data-testid="final-cta-whatsapp"
          >
            <MessageCircle size={14} /> WhatsApp
          </a>
          <Link
            to="/hizli-randevu"
            className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full border-2 border-white/60 text-white text-xs tracking-[0.18em] uppercase font-semibold hover:bg-white hover:text-[#0E2B4F] transition-colors"
            data-testid="final-cta-appointment"
          >
            <CalendarCheck size={14} /> Online Randevu
          </Link>
          {SITE.mapUrl ? (
            <a
              href={SITE.mapUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full border-2 border-white/30 text-white/85 text-xs tracking-[0.18em] uppercase font-semibold hover:border-white hover:text-white transition-colors"
              data-testid="final-cta-map"
            >
              <MapPin size={14} /> Konum
            </a>
          ) : null}
        </div>
        {data.footnote ? (
          <p className="mt-8 text-xs text-white/50 tracking-wide">{data.footnote}</p>
        ) : null}
      </div>
    </section>
  );
}

function ProductsShowcase({ data }) {
  if (!data || !Array.isArray(data.items) || data.items.length === 0) return null;
  return (
    <section data-testid="products-showcase" className="py-20 md:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-10 md:mb-12">
          <p className="chip-green mb-3">{data.kicker || "Ürün Ailesi"}</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#0B1B33] tracking-tight leading-tight">
            {data.title || "Öne Çıkan Ürünler"}
          </h2>
          {data.intro ? (
            <p className="mt-4 text-[#4B5A6E] leading-relaxed">{data.intro}</p>
          ) : null}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {data.items.map((p, i) => (
            <article
              key={i}
              className="group bg-[#F5F7FA] border border-[#E5EAF0] rounded-2xl overflow-hidden hover:bg-white hover:border-[#10B981]/40 hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              <div className="aspect-square bg-white overflow-hidden flex items-center justify-center p-6">
                {p.image ? (
                  <img
                    src={`/${p.image}`}
                    alt={p.name}
                    loading="lazy"
                    className="max-w-full max-h-full object-contain group-hover:scale-[1.04] transition-transform duration-500"
                  />
                ) : null}
              </div>
              <div className="p-5 md:p-6">
                {p.tag ? (
                  <p className="text-[10px] tracking-[0.22em] uppercase text-[#4F8B2A] font-bold mb-1.5">{p.tag}</p>
                ) : null}
                <p className="font-display text-lg md:text-xl text-[#0B1B33] font-semibold leading-snug mb-2">
                  {p.name}
                </p>
                {p.desc ? (
                  <p className="text-sm text-[#4B5A6E] leading-relaxed mb-4">{p.desc}</p>
                ) : null}
                {p.href ? (
                  <a
                    href={p.href}
                    target={p.href.startsWith("http") ? "_blank" : undefined}
                    rel={p.href.startsWith("http") ? "noreferrer" : undefined}
                    className="inline-flex items-center gap-1.5 text-xs tracking-[0.18em] uppercase text-[#0E2B4F] font-semibold uline-link"
                  >
                    {p.ctaLabel || "Detay"} <ArrowRight size={12} />
                  </a>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function PractitionersSection({ data }) {
  if (!data || !Array.isArray(data.items) || data.items.length === 0) return null;
  return (
    <section data-testid="practitioners" className="py-20 md:py-24 bg-[#F5F7FA] border-y border-[#E5EAF0]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-10 md:mb-12">
          <p className="chip-green mb-3">Uygulayıcı Ekip</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#0B1B33] tracking-tight leading-tight">
            {data.title || "Uygulamayı Kim Yapıyor?"}
          </h2>
          {data.intro ? (
            <p className="mt-4 text-[#4B5A6E] leading-relaxed">{data.intro}</p>
          ) : null}
        </div>
        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {data.items.map((p, i) => (
            <article
              key={i}
              className="group bg-white border border-[#E5EAF0] rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              <div className="aspect-[4/5] bg-[#EEF2F6] overflow-hidden">
                {p.image ? (
                  <img
                    src={`/${p.image}`}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                  />
                ) : null}
              </div>
              <div className="p-5 md:p-6">
                {p.role ? (
                  <p className="text-[10px] tracking-[0.22em] uppercase text-[#4F8B2A] font-bold mb-1.5">{p.role}</p>
                ) : null}
                <p className="font-display text-xl text-[#0B1B33] font-semibold leading-snug mb-1">
                  {p.name}
                </p>
                {p.focus ? (
                  <p className="text-sm text-[#4B5A6E] leading-relaxed">{p.focus}</p>
                ) : null}
              </div>
            </article>
          ))}

          <article className="relative bg-gradient-to-br from-[#0E2B4F] via-[#123564] to-[#0E2B4F] rounded-2xl overflow-hidden p-6 md:p-7 flex flex-col justify-between text-white min-h-[320px]">
            <div className="absolute -top-16 -right-10 h-40 w-40 rounded-full bg-[#10B981]/25 blur-3xl" aria-hidden="true" />
            <div className="relative">
              <div className="h-11 w-11 rounded-xl bg-white/10 text-[#A8C520] flex items-center justify-center mb-4">
                <CalendarCheck size={22} aria-hidden="true" />
              </div>
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#A8C520] font-bold mb-2">Randevu</p>
              <p className="font-display text-xl md:text-2xl leading-snug font-semibold mb-2">
                {data.ctaTitle || "Uygun bir zaman seçin"}
              </p>
              <p className="text-sm text-white/70 leading-relaxed">
                {data.ctaText || "Bölge, cinsiyet ve uygun günü seçerek dakikalar içinde randevunuzu oluşturun."}
              </p>
            </div>
            <div className="relative mt-6 flex flex-col gap-2">
              <Link
                to="/hizli-randevu"
                className="inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[#10B981] text-white text-xs tracking-[0.18em] uppercase font-semibold hover:bg-[#059669] transition-colors"
                data-testid="practitioners-appointment"
              >
                <CalendarCheck size={14} /> Hızlı Randevu
              </Link>
              <a
                href={waHref(`Merhaba, "${data.serviceTitle || "Lazer Epilasyon"}" için randevu almak istiyorum.`)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 h-11 rounded-full border border-white/30 text-white text-xs tracking-[0.18em] uppercase font-semibold hover:bg-white hover:text-[#0E2B4F] transition-colors"
                data-testid="practitioners-whatsapp"
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

function DetailLayout({ item, kind, related, relatedBlog }) {
  const parentLabel = kind === "service" ? "Hizmetlerimiz" : kind === "product" ? "Ürünlerimiz" : "Blog";
  const parentTo = kind === "service" ? "/hizmetlerimiz" : kind === "product" ? "/urunlerimiz" : "/blog";
  const categoryLabel = kind === "service" && item.category ? (categories[item.category]?.label || categories[item.category]?.title || null) : null;
  const isHub = item.is_category_hub === true;
  const jsonLd =
    kind === "service"
      ? {
          "@context": "https://schema.org",
          "@type": "Service",
          name: item.title,
          description: item.description || item.excerpt,
          provider: { "@type": "HealthAndBeautyBusiness", name: SITE.name, telephone: SITE.phoneRaw, address: SITE.address },
          areaServed: "Ankara, Türkiye",
          image: item.image ? `${SITE.origin}/${item.image}` : undefined,
        }
      : kind === "product"
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: item.title,
          description: item.description || item.excerpt,
          image: item.image ? (item.image.startsWith("http") ? item.image : `${SITE.origin}/${item.image.replace(/^\//, "")}`) : `${SITE.origin}/og-image.jpg`,
          brand: { "@type": "Brand", name: "PediZone" },
          sku: item.slug,
          offers: {
            "@type": "Offer",
            priceCurrency: "TRY",
            price: item.slug.includes("krem") || item.slug.includes("ureli") || item.slug.includes("mykored") ? "1799.00" : item.slug.includes("kopugu") || item.slug.includes("fresh-guard") || item.slug.includes("pedizoner") ? "1599.00" : "1699.00",
            priceValidUntil: "2027-12-31",
            itemCondition: "https://schema.org/NewCondition",
            availability: "https://schema.org/InStock",
            url: item.marketplaces?.find(m => m.name?.includes("Resmi") || m.name?.includes("Web"))?.url || `${SITE.origin}/urun/${item.slug}`,
            seller: {
              "@type": "Organization",
              name: "PediZone"
            }
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.9",
            bestRating: "5",
            worstRating: "1",
            reviewCount: item.slug.includes("krem") || item.slug.includes("ureli") || item.slug.includes("intense-repair") ? "189" : item.slug.includes("kopugu") || item.slug.includes("fresh-guard") || item.slug.includes("pedizoner") ? "114" : item.slug.includes("mykored") ? "128" : "142"
          }
        }
      : {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: item.title,
          description: item.description || item.excerpt,
          image: item.image ? `${SITE.origin}/${item.image}` : undefined,
          author: { "@type": "Organization", name: SITE.name },
          publisher: { "@type": "Organization", name: SITE.name },
        };

  const kicker = kind === "service" ? "Hizmet" : kind === "product" ? "Ürün" : "Blog Yazısı";

  const benefits = kind === "service" ? [
    "Steril, hijyen odaklı klinik ortam",
    "Deneyimli uzman kadro",
    "Kişiye özel bakım planı",
    "Kanıtlanmış cihaz & dermokozmetik ürün seçkisi",
  ] : null;

  // Combined JSON-LD: base + FAQPage (if any)
const combinedJsonLd = (() => {
  if (!jsonLd) return null;

  const faqLd = (kind === "service" || kind === "blog") ? faqJsonLd(item.faqs) : null;
  if (!faqLd) return jsonLd;

  return {
    "@context": "https://schema.org",
    "@graph": [jsonLd, faqLd],
  };
})();

  // Category breadcrumb item (only for services)
  const categoryBreadcrumb = kind === "service" && item.category && categories[item.category]
    ? [{ label: categories[item.category].label || categories[item.category].title, to: `/hizmetlerimiz?kategori=${item.category}` }]
    : [];

  return (
    <>
      <SEO
        title={item.title}
        description={item.description && item.description !== item.title ? item.description : (item.excerpt || item.title)}
        keywords={item.keywords}
        path={`/${item.slug}`}
        image={item.image}
        type={kind === "blog" ? "article" : "website"}
        jsonLd={combinedJsonLd}
      />

      <section className="pt-32 pb-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumb items={[{ label: parentLabel, to: parentTo }, ...categoryBreadcrumb, { label: item.title }]} />
      </section>

      <section className="pb-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="chip mb-4">{kicker}</p>
            <h1 data-testid="detail-title" className="font-display text-4xl md:text-6xl text-[#0B1B33] leading-[1.05] mb-6 tracking-tight">
              {item.h1 || item.title}
            </h1>
            {item.excerpt ? (
              <p className="text-[#4B5A6E] text-base md:text-lg leading-relaxed mb-6">{item.excerpt}</p>
            ) : null}
            <HeroBadges badges={item.hero_badges} />
            <div className="flex flex-wrap gap-3 mb-6">
              {item.hero_show_appointment ? (
                <Link to="/hizli-randevu" data-testid="detail-appointment" className="btn-mint">
                  <CalendarCheck size={14} /> Randevu Al
                </Link>
              ) : null}
              <a href={waHref(`Merhaba, "${item.title}" hakkında bilgi almak istiyorum.`)} target="_blank" rel="noreferrer" data-testid="detail-whatsapp" className={item.hero_show_appointment ? "btn-outline" : "btn-mint"}>
                <MessageCircle size={14} /> WhatsApp
              </a>
              <a href={`tel:${SITE.phoneRaw}`} data-testid="detail-call" className="btn-outline">
                <Phone size={12} /> {SITE.phone}
              </a>
            </div>
          </div>

          {item.image ? (
            <div className={`relative max-w-sm sm:max-w-md mx-auto md:mx-0 md:justify-self-end ${kind === "product" ? "w-full flex items-center justify-center min-h-[380px]" : "bg-white border border-[#E5EAF0] p-4 rounded-2xl shadow-lg"}`}>
              <img
                src={`/${item.image}`}
                alt={item.title}
                loading="eager"
                className="w-full h-auto max-h-[500px] object-contain rounded-xl mx-auto block"
              />
              {kind !== "product" ? <div className="absolute -bottom-4 -right-4 md:-right-6 border-2 border-[#10B981] h-24 w-24 md:h-32 md:w-32 pointer-events-none rounded-2xl -z-10" /> : null}
            </div>
          ) : null}
        </div>
      </section>

      <QuickFactsBar facts={item.quick_facts} />

      {benefits ? (
        <section className="py-12 bg-[#F5F7FA] border-y border-[#E5EAF0]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle2 size={22} className="text-[#10B981] shrink-0 mt-0.5" />
                <p className="text-sm text-[#0B1B33] font-medium leading-snug">{b}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <SuitabilitySection data={item.suitability} />
      <ProcessTimeline steps={item.process_steps} title={item.process_title} />

      <section className="py-12 md:py-16 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <ContentWithImages
          paragraphs={item.paragraphs}
          images={item.images}
          mainImage={item.image}
          title={item.title}
        />
      </section>

      {["protez-tirnak", "nail-arts", "manikur-pedikur"].includes(item.slug) ? (
        <ServiceGallerySlider images={item.images} title="Örnek İşlemler ve Uygulama Kareleri" />
      ) : null}

      {item.video ? (
        <section className="py-8 md:py-12 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-2xl bg-transparent">
            <video
              src={item.video.startsWith("http") || item.video.startsWith("/") ? item.video : `/${item.video}`}
              autoPlay
              muted
              loop
              playsInline
              className="w-full max-h-[500px] object-contain rounded-2xl mx-auto block border-0 shadow-none bg-transparent"
            />
          </div>
        </section>
      ) : null}

      <CalloutStack callouts={item.callouts} />

      {/* Service Card & Team Cards if defined */}
      {item.service_card || item.team_cards ? (
        <section className="py-16 bg-[#F5F7FA] border-y border-[#E5EAF0]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
            {item.service_card ? (
              <div className="bg-[#0E2B4F] text-white rounded-3xl p-8 md:p-10 shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <span className="inline-block px-3 py-1 rounded-full bg-[#10B981]/20 text-[#10B981] text-xs font-semibold uppercase tracking-wider mb-3 border border-[#10B981]/30">Önerilen Hizmet</span>
                  <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white mb-2">{item.service_card.title}</h3>
                  <p className="text-slate-300 text-sm md:text-base leading-relaxed max-w-2xl">{item.service_card.desc}</p>
                </div>
                <Link to={item.service_card.href} className="btn-mint shrink-0">
                  {item.service_card.ctaLabel || "Hizmeti İncele"} <ArrowRight size={14} />
                </Link>
              </div>
            ) : null}

            {item.team_cards && item.team_cards.length > 0 ? (
              <div>
                <div className="text-center mb-8">
                  <p className="chip mb-2">Uzman Kadromuz</p>
                  <h3 className="font-display text-2xl md:text-3xl text-[#0B1B33] tracking-tight">Uygulama Uzmanlarımız</h3>
                </div>
                <div className={item.team_cards.length === 1 ? "max-w-xl mx-auto" : "grid md:grid-cols-2 gap-6"}>
                  {item.team_cards.map((tc, idx) => (
                    <div key={idx} className="bg-white border border-[#E5EAF0] rounded-2xl p-7 md:p-8 shadow-lg hover:shadow-xl transition-all flex flex-col sm:flex-row items-center sm:items-center gap-6 text-center sm:text-left">
                      {tc.image ? (
                        <div className="w-32 h-32 md:w-36 md:h-36 rounded-2xl overflow-hidden shrink-0 border-2 border-[#10B981]/30 shadow-md bg-[#F5F7FA]">
                          <img src={`/${tc.image}`} alt={tc.name} className="w-full h-full object-cover object-top" />
                        </div>
                      ) : (
                        <div className="w-32 h-32 md:w-36 md:h-36 rounded-2xl bg-[#EEF6F1] text-[#10B981] flex items-center justify-center shrink-0 font-display font-bold text-2xl border-2 border-[#10B981]/30">
                          {tc.name.split(" ").map(n => n[0]).join("")}
                        </div>
                      )}
                      <div>
                        <h4 className="font-display text-2xl text-[#0B1B33] font-semibold mb-1.5">{tc.name}</h4>
                        <p className="text-xs uppercase tracking-wider text-[#10B981] font-bold mb-2.5">{tc.title}</p>
                        <p className="text-sm md:text-base text-[#4B5A6E] leading-relaxed mb-4">{tc.desc}</p>
                        <Link to={`/hakkimizda#${tc.slug}`} className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-[#10B981] font-semibold uline-link">
                          Uzmanı Tanı →
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* Free Podological Pre-evaluation Banner */}
      {(kind === "product" || (kind === "service" && item.category === "podoloji")) ? (
      <section className="py-16 bg-white border-y border-[#E5EAF0]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#0E2B4F] text-white rounded-3xl p-8 md:p-12 overflow-hidden relative shadow-2xl flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="absolute right-0 top-0 bottom-0 w-1/3 opacity-10 bg-no-repeat bg-cover pointer-events-none" style={{ backgroundImage: "url('/upload/podolog-serdar-ceylan-destek.webp')" }}></div>
            <div className="max-w-xl z-10">
              <span className="inline-block px-3 py-1 rounded-full bg-[#10B981]/20 text-[#10B981] text-xs font-semibold uppercase tracking-wider mb-4 border border-[#10B981]/30">ÜCRETSİZ PODOLOJİK DESTEK</span>
              <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tight text-white mb-4 leading-snug">
                Fotoğrafını Gönder, Podolog İncelesin
              </h2>
              <p className="text-slate-300 text-base md:text-lg mb-6 leading-relaxed">
                Ayak ve tırnak problemleriniz için uzman podologlarımızdan 24 saat içinde ücretsiz ön değerlendirme alın, size özel bakım protokolünü öğrenin.
              </p>
              <div className="flex flex-wrap gap-4 items-center">
                <a
                  href="https://medipodo.com/ucretsiz-on-degerlendirme"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#10B981] hover:bg-[#059669] text-white font-semibold px-8 py-4 rounded-xl shadow-lg transition-all text-base hover:scale-105"
                >
                  Ücretsiz Ön Değerlendirme Başlat
                  <ArrowRight size={18} />
                </a>
                <span className="text-xs text-slate-400">24 Saat İçinde Geri Dönüş</span>
              </div>
            </div>
            <div className="z-10 shrink-0">
              <div className="relative rounded-2xl overflow-hidden border-2 border-white/10 shadow-2xl max-w-[280px] md:max-w-[320px] bg-slate-800">
                <img
                  src="/upload/podolog-serdar-ceylan-destek.webp"
                  alt="Ücretsiz Ön Değerlendirme - Podolog Serdar Ceylan"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      ) : null}

      {item.marketplaces ? (
        <section className="py-12 bg-[#F5F7FA] border-y border-[#E5EAF0]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="chip-green mb-3 mx-auto">Yetkili Satış Kanalları</p>
              <h2 className="font-display text-3xl md:text-4xl text-[#0B1B33] tracking-tight leading-tight">
                Hemen Güvenle Satın Alın
              </h2>
              <p className="mt-3 text-[#4B5A6E] text-sm md:text-base leading-relaxed">
                PediZone %15 Üreli Yoğun Onarıcı Kremi üretici garantisiyle resmi web sitemizden veya yetkili pazaryerlerindeki resmi mağazalarımızdan tek tıkla sipariş verebilirsiniz.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {item.marketplaces.map((m, i) => (
                <a
                  key={i}
                  href={m.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group bg-white border border-[#E5EAF0] rounded-2xl p-6 text-center hover:border-[#10B981]/50 hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col items-center justify-between"
                >
                  <div>
                    <div className="h-14 w-14 rounded-2xl bg-[#F5F7FA] border border-[#E5EAF0] flex items-center justify-center mx-auto mb-4 group-hover:bg-[#EEF6F1] group-hover:border-[#10B981]/30 transition-colors">
                      <ShoppingBag size={24} className="text-[#0E2B4F] group-hover:text-[#10B981] transition-colors" />
                    </div>
                    <p className="font-display text-lg text-[#0B1B33] font-semibold mb-1">{m.name}</p>
                    <p className="text-xs text-[#4B5A6E] leading-relaxed mb-4">{m.badge}</p>
                  </div>
                  <span className="inline-flex items-center justify-center gap-1.5 w-full py-2.5 px-4 rounded-xl bg-[#0E2B4F] text-white text-xs tracking-[0.14em] uppercase font-semibold group-hover:bg-[#10B981] transition-colors">
                    {m.cta || "Satın Al"} <ExternalLink size={12} />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <FeatureCards features={item.feature_cards} title={item.feature_title} kicker={item.feature_kicker} />
      <WhyUsGrid items={item.why_us} title={item.why_us_title} />
      <BeforeAfterPlaceholder data={item.before_after} />
      <ProductsShowcase data={item.products} />
      <PractitionersSection data={item.practitioners ? { ...item.practitioners, serviceTitle: item.title } : null} />

      {item.slug === "erkek-lazer-epilasyon-islemi" ? (
        <section className="py-20 bg-[#F5F7FA] border-y border-[#E5EAF0]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="chip-green mb-3">Uzman Kadromuz</p>
              <h2 className="font-display text-3xl md:text-4xl text-[#0B1B33] tracking-tight">Uygulama Ekibimiz</h2>
            </div>
            <div className="bg-white border border-[#E5EAF0] rounded-2xl p-6 md:p-8 shadow-sm flex flex-col md:flex-row items-center gap-8">
              <div className="w-40 h-40 md:w-48 md:h-48 shrink-0 overflow-hidden rounded-2xl bg-[#F5F7FA] border border-[#E5EAF0] shadow-inner">
                <img src="/upload/team/semih-ceylan.webp" alt="Semih Ceylan - Epilasyon Uzmanı" className="w-full h-full object-cover object-top" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-display text-2xl text-[#0B1B33] font-semibold mb-1">Semih Ceylan</h3>
                <p className="text-[#10B981] text-xs font-bold tracking-[0.2em] uppercase mb-4">Epilasyon Uzmanı — MYK Seviye 4</p>
                <p className="text-[#4B5A6E] text-sm md:text-base leading-relaxed">
                  Uygulama öncesinde cilt ve kıl yapısının değerlendirilmesi, uygun uygulama planının oluşturulması ve işlem sürecinin profesyonel şekilde yürütülmesine önem veriyoruz.
                </p>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {(kind === "service" || kind === "blog") && item.faqs && item.faqs.length > 0 ? (
        <FAQ items={item.faqs} title={`${item.title} — Sık Sorulan Sorular`} />
      ) : null}

      {item.final_cta ? (
        <FinalCTA data={{ ...item.final_cta, serviceTitle: item.title }} />
      ) : (
        <section className="py-14 bg-[#0E2B4F]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="font-display text-2xl md:text-4xl text-white mb-4 tracking-tight">
              {kind === "service" ? "Uzmandan bilgi almak ister misiniz?" : kind === "product" ? "Ürünü almak veya kullanımı hakkında bilgi almak için" : "MediEste ile tanışın"}
            </h3>
            <p className="text-white/70 mb-8 max-w-xl mx-auto">
              WhatsApp üzerinden hızlıca yanıtlıyoruz. Size özel bakım planı için dilediğiniz zaman yazabilirsiniz.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href={waHref(`Merhaba, "${item.title}" hakkında bilgi almak istiyorum.`)} target="_blank" rel="noreferrer" className="btn-mint">
                <MessageCircle size={14} /> WhatsApp ile Yaz
              </a>
              <a href={`tel:${SITE.phoneRaw}`} className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full border-2 border-white/60 text-white text-xs tracking-[0.18em] uppercase font-semibold hover:bg-white hover:text-[#0E2B4F] transition-colors">
                <Phone size={12} /> {SITE.phone}
              </a>
            </div>
          </div>
        </section>
      )}

      {related && related.length > 0 ? (
        <section data-testid="related-services" className="py-20 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
              <div>
                <p className="chip-green mb-3">{isHub ? "Cilt Bakımı Menüsü" : "Benzer Uygulamalar"}</p>
                <h2 className="font-display text-3xl md:text-4xl text-[#0B1B33] tracking-tight leading-tight">
                  {isHub
                    ? `${categoryLabel || item.title} Uygulamalarımız`
                    : (categoryLabel ? `Diğer ${categoryLabel} Uygulamaları` : `İlgili ${kicker}ler`)}
                </h2>
              </div>
              <Link to={parentTo} className="text-sm tracking-[0.15em] uppercase text-[#4F8B2A] font-semibold uline-link">Tümünü Gör →</Link>
            </div>
            <div className="relative">
              <div className="flex overflow-x-auto snap-x snap-mandatory gap-5 pb-6 pt-2 no-scrollbar scroll-smooth">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/${r.slug}`}
                    className="group relative block overflow-hidden aspect-[3/4] rounded-2xl medi-card shrink-0 w-[260px] sm:w-[280px] md:w-[calc(25%-1.125rem)] snap-start"
                  >
                    {r.image ? <img src={`/${r.image}`} alt={r.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" /> : <div className="w-full h-full bg-[#F5F7FA]"/>}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B33]/85 via-[#0B1B33]/20 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-5">
                      <h3 className="font-display text-base md:text-lg text-white leading-tight font-medium mb-2 line-clamp-2">{r.title}</h3>
                      <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-[#10B981] font-semibold opacity-90 group-hover:opacity-100 group-hover:translate-x-1 transition-all">İncele <ArrowRight size={12} /></span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {relatedBlog && relatedBlog.length > 0 ? (
        <section data-testid="related-blog" className="py-20 md:py-24 bg-[#F5F7FA] border-y border-[#E5EAF0]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
              <div>
                <p className="chip mb-3">Bilgi Rehberi</p>
                <h2 className="font-display text-3xl md:text-4xl text-[#0B1B33] tracking-tight leading-tight">
                  Konuyla İlgili Blog Yazılarımız
                </h2>
              </div>
              <Link to="/blog" className="text-sm tracking-[0.15em] uppercase text-[#4F8B2A] font-semibold uline-link">Tüm Blog →</Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {relatedBlog.map((b) => (
                <Link
                  key={b.slug}
                  to={`/${b.slug}`}
                  className="group bg-white border border-[#E5EAF0] rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                  {b.image ? (
                    <div className="aspect-[16/10] overflow-hidden bg-[#F5F7FA]">
                      <img src={`/${b.image}`} alt={b.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                  ) : (
                    <div className="aspect-[16/10] bg-gradient-to-br from-[#A8C520]/15 to-[#3F7A2E]/10" />
                  )}
                  <div className="p-5 md:p-6">
                    <p className="text-[10px] tracking-[0.22em] uppercase text-[#4F8B2A] font-bold mb-2">Blog Yazısı</p>
                    <h3 className="font-display text-lg md:text-xl text-[#0B1B33] leading-snug font-medium mb-3">{b.title}</h3>
                    {b.excerpt && <p className="text-sm text-[#4B5A6E] leading-relaxed line-clamp-3">{b.excerpt}</p>}
                    <span className="inline-flex items-center gap-1.5 mt-4 text-xs tracking-[0.15em] uppercase text-[#4F8B2A] font-semibold">
                      Yazıyı Oku <ArrowRight size={12} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
}

function pickRelatedServices(current) {
  // Category hub pages get more cards (all children).
  const isHub = current.is_category_hub === true;
  const cap = isHub ? 12 : 4;
  // Prefer explicit related_services list; fallback to same-category; otherwise first N others.
  if (Array.isArray(current.related_services) && current.related_services.length > 0) {
    return current.related_services
      .map((slug) => services.find((s) => s.slug === slug))
      .filter(Boolean)
      .slice(0, cap);
  }
  if (current.category) {
    return services.filter((s) => s.category === current.category && s.slug !== current.slug).slice(0, cap);
  }
  return services.filter((s) => s.slug !== current.slug).slice(0, cap);
}

function pickRelatedBlog(current) {
  if (Array.isArray(current.related_blog) && current.related_blog.length > 0) {
    return current.related_blog
      .map((slug) => blog.find((b) => b.slug === slug))
      .filter(Boolean)
      .slice(0, 3);
  }
  // Heuristic: match by keyword overlap with title
  const kws = String(current.title || "").toLowerCase().split(/\s+/).filter((w) => w.length > 3);
  const scored = blog.map((b) => {
    const t = String(b.title || "").toLowerCase();
    const score = kws.reduce((acc, w) => acc + (t.includes(w) ? 1 : 0), 0);
    return { b, score };
  }).filter((x) => x.score > 0).sort((a, b) => b.score - a.score);
  return scored.slice(0, 3).map((x) => x.b);
}

export default function Detail() {
  const { slug } = useParams();
  const svc = services.find((s) => s.slug === slug);
  const prd = !svc ? products.find((p) => p.slug === slug) : null;
  const bl = !svc && !prd ? blog.find((b) => b.slug === slug) : null;

  const item = svc || prd || bl;
  if (!item) return <Navigate to="/404" replace />;

  const kind = svc ? "service" : prd ? "product" : "blog";

  let related = [];
  let relatedBlog = [];
  if (svc) {
    related = pickRelatedServices(svc);
    relatedBlog = pickRelatedBlog(svc);
  } else if (prd) {
    const otherProducts = products.filter((x) => x.slug !== slug);
    const pedizoneItems = otherProducts.filter((x) => x.slug.includes("pedizone") || x.slug.includes("krem") || x.slug.includes("kopugu") || x.slug.includes("serum"));
    const otherItems = otherProducts.filter((x) => !pedizoneItems.includes(x));
    related = [...pedizoneItems, ...otherItems].slice(0, 6);
  } else if (bl) {
    related = blog.filter((x) => x.slug !== slug).slice(0, 4);
  }

  return <DetailLayout item={item} kind={kind} related={related} relatedBlog={relatedBlog} />;
}
